import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { type FC, useCallback, useRef } from 'react';

import type { ExtendedLead } from '../../../services/types/lead';
import { NAVY, SURFACE_CONTAINER_LOWEST } from '../../../styles/tokens';
import { PENDING_CHAT_BG, PENDING_CHAT_COUNT_COLOR } from './LeadCard';
import { LeadCardSkeletonGroup } from './LeadCardSkeleton';

const KanbanColumn = styled(Box)<{ columncolor: string }>(({ columncolor }) => ({
  flex: '1 1 300px',
  minWidth: '280px',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: `${columncolor}12`,
  borderRadius: 12,
  overflow: 'hidden',
}));

const ColumnHeader = styled(Box)<{ headercolor: string }>(({ headercolor }) => ({
  padding: '12px 16px',
  backgroundColor: SURFACE_CONTAINER_LOWEST,
  borderTop: `3px solid ${headercolor}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
}));

export interface KanbanColumnConfig {
  label: string;
  color: string;
  textColor: string;
  tooltip: string;
}

interface VirtualizedKanbanColumnProps {
  config: KanbanColumnConfig;
  leads: ExtendedLead[];
  totalCount: number;
  pendingChatCount?: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  isRefreshing: boolean;
  skeletonCount: number;
  onLoadMore: () => void;
  renderLeadCard: (lead: ExtendedLead) => React.ReactNode;
  statusCountLoading: boolean;
  onToggleCollapse?: () => void;
}

const VirtualizedKanbanColumn: FC<VirtualizedKanbanColumnProps> = ({
  config,
  leads,
  totalCount,
  pendingChatCount,
  hasMore,
  isLoadingMore,
  isRefreshing,
  skeletonCount,
  onLoadMore,
  renderLeadCard,
  statusCountLoading,
  onToggleCollapse = undefined,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;
      if (scrollBottom < 200 && hasMore && !isLoadingMore) {
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  return (
    <KanbanColumn columncolor={config.color}>
      <ColumnHeader
        headercolor={config.color}
        sx={{
          '&:hover .collapse-button': {
            opacity: 1,
          },
        }}
      >
        <Tooltip title={config.tooltip} placement="top" arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: config.color,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: NAVY, fontFamily: 'Jost', letterSpacing: '0.02em' }}
            >
              {config.label}
            </Typography>
          </Box>
        </Tooltip>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onToggleCollapse && !statusCountLoading && !isRefreshing && (
            <IconButton
              className="collapse-button"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
              sx={{
                width: 24,
                height: 24,
                minWidth: 24,
                padding: 0,
                backgroundColor: 'rgba(21, 24, 58, 0.06)',
                color: NAVY,
                opacity: 0,
                transition: 'opacity 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(21, 24, 58, 0.1)',
                },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          {!statusCountLoading && pendingChatCount !== undefined && (
            <Tooltip title="Chats con respuesta pendiente del ejecutivo" placement="top" arrow>
              <Chip
                label={String(pendingChatCount).padStart(2, '0')}
                size="small"
                sx={{
                  backgroundColor: PENDING_CHAT_BG,
                  color: PENDING_CHAT_COUNT_COLOR,
                  fontWeight: 700,
                  fontFamily: 'Jost',
                  minWidth: 32,
                  height: 24,
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Total de leads en esta columna" placement="top" arrow>
            <Chip
              label={
                statusCountLoading ? (
                  <CircularProgress size={14} sx={{ color: config.textColor }} />
                ) : (
                  String(totalCount).padStart(2, '0')
                )
              }
              size="small"
              sx={{
                backgroundColor: config.color,
                color: config.textColor,
                fontWeight: 700,
                fontFamily: 'Jost',
                minWidth: 32,
                height: 24,
              }}
            />
          </Tooltip>
        </Box>
      </ColumnHeader>

      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}
      >
        {isRefreshing && leads.length === 0 && <LeadCardSkeletonGroup count={8} delay={100} />}

        {!isRefreshing && leads.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
              color: 'text.secondary',
              flex: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'Jost' }}>
              No hay leads en este estado
            </Typography>
          </Box>
        )}

        {!isRefreshing && leads.map((lead) => <Box key={lead.id}>{renderLeadCard(lead)}</Box>)}

        {isLoadingMore && skeletonCount > 0 && <LeadCardSkeletonGroup count={skeletonCount} delay={50} />}
      </Box>
    </KanbanColumn>
  );
};

export default VirtualizedKanbanColumn;
